import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Star, MessageSquare, Send, CheckCircle2, Building2, Sparkles, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const ABOUT_OPTIONS = [
  { v: "ours", label: "TridentDigitalPulse (us)", icon: Sparkles },
  { v: "external", label: "Another vendor / brand", icon: Building2 },
];

const SUBJECT_OPTIONS = [
  { v: "service", label: "Service" },
  { v: "product", label: "Product" },
  { v: "provider", label: "Person / provider" },
  { v: "platform", label: "Overall experience" },
];

const INITIAL = {
  about: "ours",
  subject_type: "service",
  subject_ref: "",
  vendor_name: "",
  rating: 5,
  comment: "",
  person_name: "",
  person_phone: "",
  person_email: "",
  city: "",
  state: "",
  is_customer: false,
};

export default function FeedbackSection() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [stats, setStats] = useState({ total: 0, by_sentiment: {}, avg_rating: 0 });

  useEffect(() => {
    api.get("/feedback/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [submitted]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.person_name || !form.comment) {
      toast.error("Please share your name and a comment.");
      return;
    }
    if (form.about === "external" && !form.vendor_name) {
      toast.error("Please tell us which vendor or brand you're rating.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/feedback/public", payload);
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const positivePct = stats.total > 0
    ? Math.round(((stats.by_sentiment?.positive || 0) / stats.total) * 100)
    : 0;

  return (
    <section
      id="feedback"
      data-testid="feedback-section"
      className="relative overflow-hidden rounded-3xl border border-brand-orange/20 bg-gradient-to-br from-brand-orange/10 via-white to-brand-yellow/15 p-6 sm:p-10 lg:p-14"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-orange/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-yellow/30 blur-3xl" />

      <div className="relative grid gap-8 lg:grid-cols-5">
        {/* Left copy + live stats */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow">
            <MessageSquare size={12} /> Feedback · Priority #1
          </div>
          <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-4xl">
            Your voice shapes<br />
            <span className="text-brand-orange">every decision we make.</span>
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
            Rate any service — <b className="text-foreground">ours or someone else's</b>.
            Every comment feeds our analytics engine, powering the dashboards that
            decide which vendors we onboard, which services we expand and where we
            focus quality efforts next.
          </p>

          {/* Live stats */}
          <div className="mt-6 grid grid-cols-3 gap-2">
            <StatMini value={stats.total} label="Reviews captured" />
            <StatMini value={`${positivePct}%`} label="Positive share" />
            <StatMini value={(stats.avg_rating || 0).toFixed(1) + "★"} label="Avg rating" />
          </div>

          <ul className="mt-6 space-y-2 text-sm">
            {[
              "Rate our services or benchmark against outside vendors.",
              "AI classifies your sentiment and routes it to the right team.",
              "See aggregated insights on our public analytics dashboard.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-brand-orange" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right form */}
        <div className="lg:col-span-3">
          {submitted ? (
            <div
              className="flex h-full flex-col items-center justify-center rounded-2xl border border-brand-orange/30 bg-white/80 p-10 text-center backdrop-blur"
              data-testid="feedback-success"
            >
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-orange text-white">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="mt-4 font-heading text-2xl font-bold">Thank you!</h3>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                Your feedback is in — it's already being analysed by our AI and will
                appear in tomorrow's insight dashboards.
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                className="mt-6 rounded-full bg-brand-ink text-white hover:bg-brand-ink/90"
                data-testid="another-feedback-btn"
              >
                Share more feedback
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-foreground/10 bg-white p-5 shadow-sm sm:p-6" data-testid="feedback-form">
              {/* About toggle */}
              <div className="mb-4">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Who are you rating?</div>
                <div className="grid grid-cols-2 gap-2 rounded-xl bg-muted p-1">
                  {ABOUT_OPTIONS.map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, about: opt.v }))}
                      data-testid={`fb-about-${opt.v}`}
                      className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        form.about === opt.v ? "bg-white shadow" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <opt.icon size={14} /> {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {form.about === "external" && (
                <div className="mb-4">
                  <Field label="Vendor / brand name *">
                    <Input
                      value={form.vendor_name}
                      onChange={set("vendor_name")}
                      placeholder="e.g. UrbanCompany, Amazon Fresh, local plumber"
                      data-testid="fb-vendor"
                      required
                    />
                  </Field>
                </div>
              )}

              {/* Rating */}
              <div className="mb-4">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Rating *</div>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, rating: n }))}
                      data-testid={`fb-star-${n}`}
                      className="p-1"
                    >
                      <Star size={30} className={n <= form.rating ? "fill-brand-yellow text-brand-yellow" : "text-muted-foreground/50"} />
                    </button>
                  ))}
                  <span className="ml-2 font-mono font-bold">{form.rating}/5</span>
                </div>
              </div>

              {/* Subject chips */}
              <div className="mb-4">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">What kind of feedback?</div>
                <div className="flex flex-wrap gap-2">
                  {SUBJECT_OPTIONS.map((s) => (
                    <button
                      key={s.v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, subject_type: s.v }))}
                      data-testid={`fb-subject-${s.v}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        form.subject_type === s.v
                          ? "border-brand-orange bg-brand-orange text-white"
                          : "border-foreground/15 bg-white text-muted-foreground hover:border-brand-orange/40 hover:text-foreground"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Which product / service?">
                  <Input value={form.subject_ref} onChange={set("subject_ref")} placeholder="e.g. Deep home cleaning" data-testid="fb-ref" />
                </Field>
                <Field label="Your name *">
                  <Input value={form.person_name} onChange={set("person_name")} placeholder="Priya Sharma" required data-testid="fb-name" />
                </Field>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Field label="Phone">
                  <Input value={form.person_phone} onChange={set("person_phone")} placeholder="10-digit mobile" data-testid="fb-phone" />
                </Field>
                <Field label="Email">
                  <Input type="email" value={form.person_email} onChange={set("person_email")} placeholder="you@example.com" data-testid="fb-email" />
                </Field>
                <Field label="City">
                  <Input value={form.city} onChange={set("city")} placeholder="Bengaluru" data-testid="fb-city" />
                </Field>
              </div>

              <div className="mt-3">
                <Field label="Tell us more *">
                  <Textarea
                    value={form.comment}
                    onChange={set("comment")}
                    placeholder={form.about === "external"
                      ? "What did you like or dislike? What could this vendor do better?"
                      : "What worked well or what could we improve? Every word helps."}
                    rows={4}
                    required
                    data-testid="fb-comment"
                  />
                </Field>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={form.is_customer}
                    onChange={() => toggle("is_customer")}
                    className="h-4 w-4 accent-brand-orange"
                    data-testid="fb-is-customer"
                  />
                  I've used TridentDigitalPulse before
                </label>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-brand-orange px-6 text-white hover:bg-brand-orange/90"
                  data-testid="fb-submit"
                >
                  <Send size={14} className="mr-1.5" />
                  {submitting ? "Submitting..." : "Submit feedback"}
                </Button>
              </div>

              <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
                <ShieldCheck size={12} className="text-brand-orange" />
                Your feedback is anonymised in public analytics.
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function StatMini({ value, label }) {
  return (
    <div className="rounded-xl border border-foreground/10 bg-white/80 px-3 py-2 text-center backdrop-blur">
      <div className="font-heading text-lg font-extrabold tracking-tight text-brand-orange">{value}</div>
      <div className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
